import { useContext, useMemo, useState } from 'react';
import * as R from 'ramda';

import LocationsAndAssetsContext from '@atom/components/common/workOrderDetail/locationsAndAssetsSection/LocationsAndAssetsContext';
import RequiredFieldsConfirmModal from '@atom/components/common/workOrderDetail/requiredFieldsConfirmModal/RequiredFieldsConfirmModal';
import { TextField } from '@atom/mui';
import { LocationDataTitle } from '@atom/types/taskLocation';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import TaskLocationSelectInput from './TaskLocationSelectInput';
import TaskLocationSidebarContext from './TaskLocationSidebarContext';

import classes from './taskLocationModal.module.css';

const styles = {
  leftInput: {
    marginRight: '0.5rem',
  },
  rightInput: {
    marginLeft: '0.5rem',
  },
};

const TaskLocationUdotInputs = () => {
  const { workOrderDetail, task, refetch, dataCySuffix } = useContext(
    LocationsAndAssetsContext,
  );
  const {
    values,
    options,
    updateValue,
    loading,
    autoFillMilepost,
    showRanges,
    isRangeError,
    rangeSubtext,
    rangeSubtextStyles,
    handleMilepostChange,
  } = useContext(TaskLocationSidebarContext);

  const [openFieldsConfirm, setOpenFieldsConfirm] = useState<boolean>(false);

  const isMilepostDisabled = useMemo(() => {
    const stationValue = values[LocationDataTitle.STATION];
    const routeValue = values[LocationDataTitle.ROUTE];

    return (
      R.any(isNilOrEmpty)([stationValue, routeValue]) ||
      workOrderDetail?.isClosed ||
      task.isCompleted
    );
  }, [workOrderDetail, task, values, options]);

  const onProceedComplete = async () => {
    await refetch();

    setOpenFieldsConfirm(false);
  };

  const onClick =
    task?.isCompleted && task?.requireAtLeastOneLocation
      ? () => setOpenFieldsConfirm(true)
      : () => {};

  const disableSelectInput = workOrderDetail?.isClosed || task.isCompleted;

  return (
    <>
      <div className={classes.inputSection} onClick={onClick}>
        <div className={classes.inputRow}>
          <TaskLocationSelectInput
            property={LocationDataTitle.STATION}
            options={options[LocationDataTitle.STATION]}
            value={values[LocationDataTitle.STATION]}
            updateValue={updateValue}
            loading={loading}
            disabled={disableSelectInput}
            onClick={onClick}
            dataCy={`locationStation-${dataCySuffix}`}
          />
        </div>
        <div className={classes.inputRow}>
          <TaskLocationSelectInput
            property={LocationDataTitle.ROUTE}
            options={options[LocationDataTitle.ROUTE]}
            value={values[LocationDataTitle.ROUTE]}
            updateValue={updateValue}
            loading={loading}
            disabled={disableSelectInput}
            onClick={onClick}
            dataCy={`locationRoute-${dataCySuffix}`}
          />
        </div>
        <div className={classes.inputRow}>
          <TextField
            type="number"
            value={values[LocationDataTitle.START_MILEPOST]}
            label={`${LocationDataTitle.START_MILEPOST} *`}
            onChange={event =>
              handleMilepostChange(
                LocationDataTitle.START_MILEPOST,
                event.target.value,
              )
            }
            onBlur={() => autoFillMilepost(LocationDataTitle.START_MILEPOST)}
            style={styles.leftInput}
            error={isRangeError}
            disabled={isMilepostDisabled}
            onClick={onClick}
            data-cy={`locationStartMilepost-${dataCySuffix}`}
          />
          <TextField
            type="number"
            value={values[LocationDataTitle.END_MILEPOST]}
            label={`${LocationDataTitle.END_MILEPOST} *`}
            onChange={event =>
              handleMilepostChange(
                LocationDataTitle.END_MILEPOST,
                event.target.value,
              )
            }
            onBlur={() => autoFillMilepost(LocationDataTitle.END_MILEPOST)}
            style={styles.rightInput}
            error={isRangeError}
            disabled={isMilepostDisabled}
            onClick={onClick}
            data-cy={`locationEndMilepost-${dataCySuffix}`}
          />
        </div>
        {showRanges && (
          <div style={rangeSubtextStyles} className={classes.rangeSubtext}>
            {rangeSubtext}
          </div>
        )}
      </div>
      <RequiredFieldsConfirmModal
        open={openFieldsConfirm}
        onCancel={() => setOpenFieldsConfirm(false)}
        onComplete={onProceedComplete}
        task={task}
        workOrderId={workOrderDetail?.id}
      />
    </>
  );
};

export default TaskLocationUdotInputs;
